<template>
  <div>
    <!-- ローディングアニメーション -->
    <div id="loading">
      <div class="spinner"></div>
      <div>読み込み中...</div>
    </div>

    <!-- タイトルにIDを追加 -->
    <h1 id="pageTitle">月齢とあなたのバイオリズム</h1>

    <!-- 支払いモーダルと通常コンテンツの条件付き表示 -->
    <div v-if="isExpired" class="modal-overlay">
      <div class="modal-content">
        <h2>料金支払いのお願い</h2>
        <p>ご利用料金のお支払いが必要です。お手続きはこちらからお願いいたします。</p>
        <a :href="paymentUrl" target="_blank" class="payment-button">お支払いへ進む</a>
      </div>
    </div>
    <div v-else>
      <!-- グラフ表示用 -->
      <div class="chart-container">
        <canvas id="moonChart"></canvas>
      </div>

      <!-- 占い結果などテキスト表示用 -->
      <div class="response-text" id="responseArea"></div>

      <!-- アイコン一覧テーブル（非表示） -->
      <div class="icon-table-container">
        <h2>今週の月齢アイコン</h2>
        <table class="icon-table">
          <thead>
            <tr>
              <th>日付</th>
              <th>アイコン</th>
              <th>月齢</th>
              <th>こよみ</th>
            </tr>
          </thead>
          <tbody id="iconTableBody"></tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'App',
  data() {
    return {
      userBirthday: '1980-05-12', // 初期値（サーバから上書きされる）
      liffId: '2006805747-AogRd1O4', // あなたのLIFF ID
      isExpired: false, // 有効期限切れフラグ
      paymentUrl: '', // 支払いURL
    };
  },
  methods: {
    /***************************************
     * LIFF 初期化と lineuid および nickname 取得
     ***************************************/
    async initializeLIFF() {
      if (!window.liff) {
        console.error('LIFF SDKが読み込まれていません。');
        document.getElementById('responseArea').textContent =
          'エラーが発生しました。もう一度お試しください。';
        document.getElementById('loading').style.display = 'none';
        return { lineuid: null, nickname: null };
      }
      try {
        await window.liff.init({ liffId: this.liffId });
        if (!window.liff.isLoggedIn()) {
          window.liff.login();
        }
        const profile = await window.liff.getProfile();
        const lineuid = profile.userId;
        const nickname = profile.displayName || 'ユーザー';
        console.log('LINE User ID:', lineuid);
        console.log('LINE Nickname:', nickname);
        return { lineuid, nickname };
      } catch (error) {
        console.error('LIFF初期化に失敗しました:', error);
        document.getElementById('responseArea').textContent =
          'LIFFの初期化に失敗しました。';
        document.getElementById('loading').style.display = 'none';
        return { lineuid: null, nickname: null };
      }
    },

    /***************************************
     * 月齢アイコンの代替 CSS ベースの表現
     ***************************************/
    getMoonPhasePercentage(age) {
      // 月齢は0から29.53日
      return (age % 29.53) / 29.53;
    },
    getConicGradient(phaseFraction) {
      return `conic-gradient(
        yellow ${phaseFraction * 100}%,
        black ${phaseFraction * 100}%,
        black 100%
      )`;
    },

    /********************************************
     * JST の現在日 "MM/DD" (x軸ハイライト用)
     ********************************************/
    getTodayLabelJST() {
      const now = new Date();
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      const jstTime = new Date(utc + 9 * 3600000);
      const mm = String(jstTime.getMonth() + 1).padStart(2, '0');
      const dd = String(jstTime.getDate()).padStart(2, '0');
      return `${mm}/${dd}`;
    },

    /********************************************
     * バイオリズムを算出する簡易サンプル関数（フォールバック用）
     ********************************************/
    calcBiorhythm(birthdate, dateStr) {
      try {
        const nowYear = new Date().getFullYear();
        const bd = new Date(birthdate);
        const [mm, dd] = dateStr.split('/');
        const targetDate = new Date(nowYear, parseInt(mm) - 1, parseInt(dd));

        const diffTime = targetDate - bd;
        const diffDays = Math.floor(diffTime / (1000 * 3600 * 24));
        const val = 20 + 10 * Math.sin((2 * Math.PI * diffDays) / 28);
        return val;
      } catch (err) {
        console.warn('calcBiorhythm error:', err);
        return 20; // fallback
      }
    },

    /********************************************
     * API を axios で呼び出しグラフ＆テキスト＋テーブル表示
     ********************************************/
    async fetchDataAndRender(lineuid, nickname) {
      if (!lineuid) return;

      // Lambda側に送るクエリパラメータ（biorhythm は不要）
      const apiUrl = `https://tpenzh9ay6.execute-api.ap-northeast-1.amazonaws.com/v1/moon?lineuid=${lineuid}`;

      try {
        const response = await axios.get(apiUrl);
        const outerData = response.data;

        let data;
        if (typeof outerData.body === 'string') {
          data = JSON.parse(outerData.body);
        } else {
          data = outerData;
        }

        console.log('parsed data:', data);

        // 追加機能: 有効期限チェック
        if (data.expired) {
          this.isExpired = true;
          this.paymentUrl = data.payment_url;
          // ローディングを非表示
          document.getElementById('loading').style.display = 'none';
          return;
        }

        // ★ サーバから誕生日が来たら上書き（個別化）
        if (data.user_config && data.user_config.birthday) {
          this.userBirthday = data.user_config.birthday; // 'YYYY-MM-DD'
        }

        // テキスト表示部分
        const bedrockResponse = data.bedrock_response || '(占い結果の取得に失敗)';
        document.getElementById('responseArea').innerHTML = bedrockResponse;

        // 1週間分データ
        const weekData = data.week_data || [];
        const labels = weekData.map((d) => d.date);

        // (A) 月齢データ：CSSベースのプロット
        const moonPoints = weekData.map((d) => {
          const phaseFraction = this.getMoonPhasePercentage(d.moonAge);
          return {
            x: d.date,
            y: d.moonAge,
            phaseFraction: phaseFraction,
          };
        });

        // (B) バイオリズム用のデータ：サーバ計算値があれば優先
        let biorhythmPoints;
        if (Array.isArray(data.biorhythm_week) && data.biorhythm_week.length) {
          const mapByDate = new Map(
            data.biorhythm_week.map((r) => [r.date, r.value])
          );
          biorhythmPoints = weekData.map((d) => ({
            x: d.date,
            y: mapByDate.get(d.date) ?? this.calcBiorhythm(this.userBirthday, d.date),
          }));
        } else {
          // フォールバック：従来計算
          biorhythmPoints = weekData.map((d) => ({
            x: d.date,
            y: this.calcBiorhythm(this.userBirthday, d.date),
          }));
        }

        // グラフ描画
        const ctx = document.getElementById('moonChart').getContext('2d');

        // カスタムポイント描画プラグイン
        const customPointPlugin = {
          id: 'customPointPlugin',
          afterDatasetsDraw: (chart) => {
            const { ctx } = chart;
            chart.data.datasets.forEach((dataset, datasetIndex) => {
              if (dataset.label === '月齢') {
                const meta = chart.getDatasetMeta(datasetIndex);
                meta.data.forEach((point, index) => {
                  const phaseFraction = moonPoints[index].phaseFraction;
                  const xPos = point.x;
                  const yPos = point.y;
                  const radius = 10;
                  const gradient = ctx.createConicGradient(0, xPos, yPos);
                  gradient.addColorStop(0, 'yellow');
                  gradient.addColorStop(phaseFraction, 'yellow');
                  gradient.addColorStop(phaseFraction, 'black');
                  gradient.addColorStop(1, 'black');
                  ctx.fillStyle = gradient;
                  ctx.beginPath();
                  ctx.arc(xPos, yPos, radius, 0, 2 * Math.PI);
                  ctx.fill();
                });
              }
            });
          },
        };

        // 今日のx軸をハイライト
        const todayHighlightPlugin = {
          id: 'todayHighlightPlugin',
          afterDraw: (chart) => {
            const { ctx, scales } = chart;
            const xScale = scales.x;
            const yScale = scales.y;
            const todayLabel = this.getTodayLabelJST();

            const labelIndex = xScale.ticks.findIndex(
              (tick) => tick.label === todayLabel
            );
            if (labelIndex === -1) return;

            const xPosition = xScale.getPixelForTick(labelIndex);
            let highlightWidth;
            if (labelIndex < xScale.ticks.length - 1) {
              const nextX = xScale.getPixelForTick(labelIndex + 1);
              highlightWidth = nextX - xPosition;
            } else {
              highlightWidth =
                xScale.getPixelForTick(labelIndex) -
                xScale.getPixelForTick(labelIndex - 1);
            }

            const rectX = xPosition - highlightWidth / 2;
            const rectY = yScale.top;
            const rectHeight = yScale.bottom - yScale.top;

            ctx.fillStyle = 'rgba(255, 255, 0, 0.1)';
            ctx.fillRect(rectX, rectY, highlightWidth, rectHeight);

            ctx.strokeStyle = 'yellow';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(xPosition, yScale.bottom);
            ctx.lineTo(xPosition, yScale.bottom + 10);
            ctx.stroke();
          },
        };

        // Chart.js プラグイン登録
        window.Chart.register(customPointPlugin);
        window.Chart.register(todayHighlightPlugin);

        new window.Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: '月齢',
                data: moonPoints,
                type: 'line',
                backgroundColor: 'rgba(255, 255, 0, 0.7)',
                borderColor: 'rgba(255, 255, 0, 0.9)',
                borderWidth: 2,
                tension: 0.1,
                pointRadius: 0,
                yAxisID: 'y',
              },
              {
                label: 'バイオリズム',
                data: biorhythmPoints,
                type: 'line',
                backgroundColor: 'rgba(255, 127, 127, 0.3)',
                borderColor: 'rgba(255, 127, 127, 0.9)',
                borderWidth: 2,
                tension: 0.1,
                pointRadius: 3,
                yAxisID: 'y',
              },
            ],
          },
          options: {
            interaction: {
              mode: 'index',
              intersect: false,
            },
            scales: {
              x: {
                ticks: {
                  color: (context) => {
                    const todayLabel = this.getTodayLabelJST();
                    return context.tick.label === todayLabel
                      ? 'yellow'
                      : 'white';
                  },
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.2)',
                },
              },
              y: {
                type: 'linear',
                position: 'left',
                ticks: {
                  color: 'white',
                },
                grid: {
                  color: 'rgba(255, 255, 255, 0.2)',
                },
                title: {
                  display: true,
                  text: '月齢 / バイオリズム',
                  color: 'white',
                },
              },
            },
            plugins: {
              legend: {
                labels: { color: 'white' },
              },
              tooltip: {
                bodyColor: '#fff',
                titleColor: '#fff',
              },
            },
          },
        });

        // (D) テーブル生成（非表示だが維持）
        const iconTableBody = document.getElementById('iconTableBody');
        iconTableBody.innerHTML = '';
        weekData.forEach((d) => {
          const tr = document.createElement('tr');

          // 日付
          const tdDate = document.createElement('td');
          tdDate.textContent = d.date;
          tr.appendChild(tdDate);

          // アイコン（CSSベース）
          const tdIcon = document.createElement('td');
          const moonDiv = document.createElement('div');
          moonDiv.classList.add('moon-phase');
          const phaseFraction = this.getMoonPhasePercentage(d.moonAge);
          moonDiv.style.setProperty('--phase', `${phaseFraction * 100}%`);
          moonDiv.style.background = this.getConicGradient(phaseFraction);
          tdIcon.appendChild(moonDiv);
          tr.appendChild(tdIcon);

          // 月齢
          const tdMoonAge = document.createElement('td');
          tdMoonAge.textContent = d.moonAge.toFixed(2);
          tr.appendChild(tdMoonAge);

          // こよみ
          const tdKoyomi = document.createElement('td');
          tdKoyomi.textContent = d.koyomi || '';
          tr.appendChild(tdKoyomi);

          iconTableBody.appendChild(tr);
        });

        // タイトルを更新
        if (nickname) {
          const pageTitle = document.getElementById('pageTitle');
          pageTitle.textContent = `月齢と${nickname}様のバイオリズム`;
          document.title = `月齢と${nickname}のバイオリズム`;
        }
      } catch (error) {
        console.error('Fetch or chart rendering error:', error);
        document.getElementById('responseArea').textContent =
          'エラーが発生しました。もう一度お試しください。';
      } finally {
        // ローディングを非表示
        document.getElementById('loading').style.display = 'none';
      }
    },
  },
  async mounted() {
    const { lineuid, nickname } = await this.initializeLIFF();
    if (lineuid) {
      await this.fetchDataAndRender(lineuid, nickname);
    }
  },
};
</script>

<!-- スタイルは別ファイル App.css で読み込むためここでは何も記述しない -->
<style src="./App.css"></style>

<style scoped>
/* モーダルスタイル */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #fff;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  width: 80%;
  max-width: 400px;
  color: #000;
  font-family: Arial, sans-serif;
}

.modal-content h2 {
  margin-bottom: 20px;
  color: #000;
}

.modal-content p {
  margin-bottom: 30px;
  color: #333;
}

.payment-button {
  display: inline-block;
  padding: 10px 20px;
  font-size: 16px;
  color: white;
  background-color: #007bff;
  text-decoration: none;
  border-radius: 5px;
  margin-top: 20px;
}

.payment-button:hover {
  background-color: #0056b3;
}

/* ローディングアニメーションのスタイル */
#loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.spinner {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 2s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* モーダルのテキストを見やすくするための追加スタイル */
.modal-content a.payment-button {
  font-weight: bold;
}
</style>
